import { useCallback, useContext, useMemo, useState } from 'react';
import * as R from 'ramda';

import { Icon, Menu, Modal, Snackbar } from '@atom/mui';
import { ContentCopyIcon, ContentPasteIcon, DeleteIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { AssetAttribute } from '@atom/types/asset';
import textDisplayUtilities from '@atom/utilities/textDisplayUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import InventoryDetailContext from '../InventoryDetailContext';

type AssetOptionMenuProps = {
  setOpenDeleteModal: (open: boolean) => void;
  canDelete: boolean;
};

type AssetCopyPayload = {
  assetId: string;
  schemaId: string;
  attributes: {
    [id: string]: {
      value: any;
      newValue: any;
    };
  };
};

const styles = {
  icon: {
    marginRight: '1rem',
  },
};

const ASSET_COPY_PASTE_KEY = 'ASSET_COPY_PASTE_KEY';

const readCopiedPayload = (): AssetCopyPayload =>
  JSON.parse(window.sessionStorage.getItem(ASSET_COPY_PASTE_KEY) || '{}');

const AssetOptionMenu = ({
  setOpenDeleteModal,
  canDelete,
}: AssetOptionMenuProps) => {
  const { asset, refetch, updateAttributes } = useContext(
    InventoryDetailContext,
  );

  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [copiedPayload, setCopiedPayload] =
    useState<AssetCopyPayload>(readCopiedPayload);

  const attributes = useMemo(
    (): AssetAttribute[] =>
      (asset.attributeGroups || []).flatMap(group => group.attributes),
    [asset.attributeGroups],
  );

  const attributesById = useMemo(
    () => R.indexBy(R.prop('id'), attributes),
    [attributes],
  );

  const copyAsset = useCallback(() => {
    const copiedAttributes = attributes
      .filter(attribute => attribute.isEditable)
      .reduce<AssetCopyPayload['attributes']>((acc, attribute) => {
        return {
          ...acc,
          [attribute.id]: {
            ...(!R.isNil(attribute.value) && {
              value: attribute.value,
            }),
            ...(!R.isNil(attribute.newValue) && {
              newValue: attribute.newValue,
            }),
          },
        };
      }, {});

    const payload: AssetCopyPayload = {
      assetId: asset.id,
      schemaId: asset.schemaId,
      attributes: copiedAttributes,
    };

    window.sessionStorage.setItem(
      ASSET_COPY_PASTE_KEY,
      JSON.stringify(payload),
    );
    setCopiedPayload(payload);

    Snackbar.info({
      message: `Copied ${textDisplayUtilities.toOwnership(
        asset.name,
      )} data to clipboard.`,
    });
  }, [asset, attributes]);

  const pasteAsset = async () => {
    setLoading(true);

    const updatedAttributes = Object.entries(copiedPayload.attributes).reduce(
      (acc, [id, update]) => {
        const currentValue = attributesById[id]?.value || null;
        const currentPendingValue = attributesById[id]?.newValue;

        const isCurrentValueUpdated = currentValue !== update.value;
        const isPendingValueUpdated =
          (!R.isNil(update.newValue) || !R.isNil(currentPendingValue)) &&
          currentPendingValue !== update.newValue;

        // if both value and newValue would stay the same omit from the request payload
        if (!isCurrentValueUpdated && !isPendingValueUpdated) {
          return acc;
        }

        return { ...acc, [id]: update };
      },
      {},
    );

    try {
      if (!R.isEmpty(updatedAttributes)) {
        await updateAttributes({
          assetId: asset.id,
          attributes: updatedAttributes,
        });
        await refetch();
      }

      Snackbar.info({ message: `Pasted data to ${asset.name}.` });
      setOpen(false);
    } catch (error) {
      Snackbar.error({ message: `Failed to paste data to ${asset.name}.` });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Menu
        IconProps={{ color: colors.neutral.white }}
        dataCyLabel="inventoryDetail-options-button"
      >
        <Menu.MenuItem
          data-cy="inventoryDetail-options-copy-button"
          onClick={copyAsset}
          disabled={isNilOrEmpty(attributes)}
        >
          <Icon
            style={styles.icon}
            color={colors.neutral.gray}
            icon={ContentCopyIcon}
          />
          Copy Data To Clipboard
        </Menu.MenuItem>
        <Menu.MenuItem
          data-cy="inventoryDetail-options-paste-button"
          disabled={
            isNilOrEmpty(copiedPayload) ||
            copiedPayload?.assetId === asset.id ||
            copiedPayload.schemaId !== asset.schemaId
          }
          onClick={() => setOpen(true)}
        >
          <Icon
            style={styles.icon}
            color={colors.neutral.gray}
            icon={ContentPasteIcon}
          />
          Paste Data
        </Menu.MenuItem>
        <Menu.MenuItem
          data-cy="inventoryDetail-options-delete-button"
          disabled={!canDelete}
          onClick={() => setOpenDeleteModal(true)}
        >
          <Icon
            style={styles.icon}
            color={colors.neutral.gray}
            icon={DeleteIcon}
          />
          Delete
        </Menu.MenuItem>
      </Menu>
      <Modal
        title="Paste Data?"
        open={open}
        onCancel={() => setOpen(false)}
        onConfirm={pasteAsset}
        confirmButtonText="Paste"
        loading={loading}
        ConfirmButtonProps={{ 'data-cy': 'inventoryDetailPasteConfirmButton' }}
      >
        <div>
          All existing attributes on <strong>{asset.name}</strong> will be
          overwritten with the copied data. This action cannot be undone. Are
          you sure?
        </div>
      </Modal>
    </>
  );
};

export default AssetOptionMenu;
